import { Component, effect, inject, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DialogShellComponent, SelectFieldComponent, TextFieldComponent } from '@custom-components';
import {
  FilledButtonComponent,
  OutlinedButtonComponent,
  TextButtonComponent,
} from '@custom-components/general/button';
import { SkeletonBlockComponent } from '@custom-components/general/skeleton/skeleton-block/skeleton-block.component';
import { FetchImageDirective } from '@core/directives/fetch-image.directive';
import {
  AssignUserRoleModel,
  CreationUserPayloadModel,
  PartnerUserModel,
  RoleFeatureResponseModel,
  RoleModel,
} from '@core/models';
import { TranslateModule } from '@ngx-translate/core';
import { UserStore } from '@root/app/store/user.store';
import { environment } from '@root/environments/environment';
import { Observable } from 'rxjs';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import {
  humanizeRoleName,
  roleLabelOrFallback,
  toRoleTranslationSegment,
} from '@core/utils/role-translation.util';

enum CreateStepEnum {
  UserInformation,
  UserRights,
  Complete,
}

type UserDialogData = {
  partnerOid: string;
  roles?: RoleModel[];
  mode: 'create' | 'edit';
  user?: PartnerUserModel;
};

@Component({
  selector: 'xemex-user-dialog',
  imports: [
    DialogShellComponent,
    FetchImageDirective,
    FilledButtonComponent,
    FormsModule,
    MatIconModule,
    MatSelectModule,
    OutlinedButtonComponent,
    ReactiveFormsModule,
    SelectFieldComponent,
    SkeletonBlockComponent,
    SpinnerComponent,
    TextButtonComponent,
    TextFieldComponent,
    TranslateModule,
  ],
  templateUrl: './user-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './user-dialog.component.scss',
})
export class UserDialogComponent {
  readonly dialogRef = inject(MatDialogRef<UserDialogComponent>);
  readonly userStore = inject(UserStore);

  readonly userForm = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    phoneNumber: new FormControl(''),
    userRole: new FormControl<RoleModel | undefined>(undefined),
  });

  roles: RoleModel[] = [];
  roleFeatures: RoleFeatureResponseModel[] = [];
  partnerOid = '';

  readonly mode: 'create' | 'edit';
  readonly user?: PartnerUserModel;

  readonly CreateStepEnum = CreateStepEnum;
  readonly toRoleKey = toRoleTranslationSegment;
  readonly humanize = humanizeRoleName;
  readonly labelOrFallback = roleLabelOrFallback;
  stepSelected = CreateStepEnum.UserInformation;
  isSubmitting = false;
  submitError = false;
  readonly companyName = environment.branding.text.company.name;

  get isEdit(): boolean {
    return this.mode === 'edit';
  }

  get email(): string | null {
    return this.userForm.controls.email.value;
  }

  get fullName(): string {
    return `${this.userForm.controls.firstName.value ?? ''} ${this.userForm.controls.lastName.value ?? ''}`.trim();
  }

  constructor(@Inject(MAT_DIALOG_DATA) data: UserDialogData) {
    this.partnerOid = data.partnerOid;
    this.mode = data.mode ?? 'create';
    this.user = data.user;

    this.userStore.getRolesMethod();

    this.roles = data.roles ?? this.userStore.roleData().roles;

    if (this.isEdit) {
      this.stepSelected = CreateStepEnum.UserRights;
    }

    effect(() => {
      this.roles = this.userStore.roleData().roles;

      if (this.roles.length && !this.userForm.controls.userRole.value) {
        const initialRole =
          this.isEdit && this.user
            ? (this.roles.find(role => role.roleId === this.user!.roleId) ?? this.roles[0])
            : this.roles[0];
        this.userForm.controls.userRole.setValue(initialRole);
      }
    });

    effect(() => {
      if (this.userStore.roleFeatures()) {
        this.roleFeatures = this.userStore.roleFeatures();
      }
    });

    this.userForm.controls.userRole.valueChanges.subscribe(role => {
      if (role?.roleId) {
        this.userStore.getRoleFeaturesMethod(role.roleId);
      }
    });
  }

  onNext(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.stepSelected = CreateStepEnum.UserRights;
  }

  onSubmit(): void {
    if (this.isSubmitting) {
      return;
    }

    if (this.isEdit) {
      if (!this.user) {
        return;
      }

      const payload: AssignUserRoleModel = {
        roleId: `${this.userForm.controls.userRole.value?.roleId}`,
        userObjectId: this.user.objectId,
        partnerObjectId: this.partnerOid,
      };
      this.send(this.userStore.assignUserRoleMethod(payload));
      return;
    }

    const userCreate: CreationUserPayloadModel = {
      partner_object_id: this.partnerOid,
      firstName: this.userForm.value.firstName!,
      lastName: this.userForm.value.lastName!,
      email: this.userForm.value.email!,
      roles: [(this.userForm.value.userRole! as RoleModel).roleName],
    };

    if (this.userForm.value.phoneNumber?.length) {
      userCreate.phone = this.userForm.value.phoneNumber;
    }

    this.send(this.userStore.createUserMethod(userCreate));
  }

  private send(request: Observable<unknown>): void {
    this.isSubmitting = true;
    this.submitError = false;

    request.subscribe({
      next: () => {
        this.isSubmitting = false;
        this.stepSelected = CreateStepEnum.Complete;
      },
      error: () => {
        this.isSubmitting = false;
        this.submitError = true;
      },
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onAddAnotherUser(): void {
    this.stepSelected = CreateStepEnum.UserInformation;
    this.submitError = false;
    this.userForm.reset();
  }

  protected compareRole = (a: RoleModel | undefined, b: RoleModel | undefined): boolean =>
    a?.roleId === b?.roleId;

  protected hasError(controlName: keyof typeof this.userForm.controls, errorCode: string): boolean {
    const control = this.userForm.controls[controlName];
    return control.hasError(errorCode) && (control.touched || control.dirty);
  }
}
